import axios from "axios";
import JSZip from "jszip";
import { $user } from "../helper/UserFactory";
import { getFingerprintHeaders } from "../helper/apiUtils";
import { API_ROOT, BASE_URL, getFileUrl } from "../helper/constants";

const MIME_EXT = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/svg+xml": "svg",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "application/pdf": "pdf",
};

export function canPickDirectory() {
  return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
}

export function isVisualMediaItem(item) {
  return item?.messageType === "image" || item?.messageType === "video";
}

// 0 = Media, 1 = Docs, 2 = All (both).
export function itemMatchesMediaTab(item, tabIndex) {
  if (tabIndex === 2) return isVisualMediaItem(item) || item?.messageType === "doc";
  return tabIndex === 0 ? isVisualMediaItem(item) : item?.messageType === "doc";
}

function sanitizeFileName(name) {
  const cleaned = String(name || "file")
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.slice(0, 180) || "file";
}

function extensionFromPath(path) {
  const clean = String(path || "").split("?")[0];
  const ext = clean.split(".").pop();
  if (ext && ext !== clean && ext.length <= 5 && /^[a-z0-9]+$/i.test(ext)) {
    return ext.toLowerCase();
  }
  return "";
}

function extensionFromMime(mime) {
  if (!mime) return "";
  if (MIME_EXT[mime]) return MIME_EXT[mime];
  if (mime.startsWith("image/")) return mime.split("/")[1] || "jpg";
  if (mime.startsWith("video/")) return mime.split("/")[1] || "mp4";
  return "";
}

function uniqueFileName(name, usedNames) {
  let candidate = name;
  let counter = 1;
  while (usedNames.has(candidate.toLowerCase())) {
    const dot = name.lastIndexOf(".");
    const base = dot === -1 ? name : name.slice(0, dot);
    const ext = dot === -1 ? "" : name.slice(dot);
    candidate = `${base} (${counter})${ext}`;
    counter += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

export function getMediaDownloadName(item, blob, usedNames) {
  const rawPath = getFileUrl(item?.fileUrl);
  let name = item?.attachedFileName || "";
  if (!name) {
    name = rawPath ? rawPath.split("/").pop() : "";
  }
  name = sanitizeFileName(name.split("?")[0] || "");

  const ext =
    extensionFromPath(name) ||
    extensionFromPath(rawPath) ||
    extensionFromMime(item?.mimeType) ||
    extensionFromMime(blob?.type) ||
    (item?.messageType === "image" ? "jpg" : item?.messageType === "video" ? "mp4" : "bin");

  if (!extensionFromPath(name)) {
    name = `${name || (item?.messageType === "doc" ? "document" : "file")}.${ext}`;
  }

  return uniqueFileName(name, usedNames);
}

function getChatDownloadUrl(item) {
  const path = getFileUrl(item?.fileUrl);
  if (!path) return "";
  if (path.startsWith("blob:") || path.startsWith("data:") || path.startsWith("http")) {
    return path;
  }
  return BASE_URL + path;
}

async function fetchEmailBlob(item) {
  const token = $user.getToken();
  const res = await fetch(`${API_ROOT}/email/attachments/${item.attachmentId}/download`, {
    headers: {
      Authorization: `jwt ${token}`,
      ...getFingerprintHeaders(),
    },
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  return res.blob();
}

async function fetchChatBlob(item, onProgress) {
  const url = getChatDownloadUrl(item);
  if (!url) throw new Error("Missing file URL");
  const response = await axios({
    url,
    method: "GET",
    responseType: "blob",
    onDownloadProgress: (progressEvent) => {
      if (!progressEvent.total || !onProgress) return;
      const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress(percent);
    },
  });
  return response.data;
}

async function fetchMediaBlob(item, onProgress) {
  if (item?.source === "email") {
    if (onProgress) onProgress(15);
    const blob = await fetchEmailBlob(item);
    if (onProgress) onProgress(100);
    return blob;
  }
  return fetchChatBlob(item, onProgress);
}

export async function ensureWritePermission(dirHandle) {
  if (!dirHandle?.queryPermission || !dirHandle?.requestPermission) return true;
  const opts = { mode: "readwrite" };
  if ((await dirHandle.queryPermission(opts)) === "granted") return true;
  return (await dirHandle.requestPermission(opts)) === "granted";
}

async function writeBlobToDirectory(dirHandle, fileName, blob) {
  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function triggerBrowserDownload(blob, fileName) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

async function downloadAsZip(items, onItemProgress) {
  const zip = new JSZip();
  const usedNames = new Set();
  let added = 0;

  for (const item of items) {
    try {
      const blob = await fetchMediaBlob(item, (percent) => {
        if (onItemProgress) onItemProgress(item, percent, false);
      });
      zip.file(getMediaDownloadName(item, blob, usedNames), blob);
      added += 1;
      if (onItemProgress) onItemProgress(item, 100, true);
    } catch (err) {
      console.error("Media zip add failed", err);
      if (onItemProgress) onItemProgress(item, 100, true);
    }
  }

  if (added === 0) return { cancelled: false, mode: "zip", saved: 0 };
  const zipBlob = await zip.generateAsync({ type: "blob" });
  triggerBrowserDownload(zipBlob, `kjuup-media-${Date.now()}.zip`);
  return { cancelled: false, mode: "zip", saved: added };
}

/**
 * Must be called from a user-gesture handler. Returns a directory handle
 * when the File System Access API is available, or null to fall back to ZIP.
 */
export async function pickDownloadDirectory() {
  if (!canPickDirectory()) return { cancelled: false, dirHandle: null };
  try {
    const dirHandle = await window.showDirectoryPicker({ mode: "readwrite" });
    return { cancelled: false, dirHandle };
  } catch (err) {
    if (err?.name === "AbortError") {
      return { cancelled: true, dirHandle: null };
    }
    return { cancelled: false, dirHandle: null };
  }
}

/**
 * Save selected media into a user-chosen folder (Chromium File System Access API).
 * Falls back to a single ZIP download when no folder handle is provided or write is denied.
 * Returns { cancelled, mode: "folder"|"zip", saved }.
 */
export async function downloadMediaSelection({ items, dirHandle = null, onItemProgress } = {}) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) return { cancelled: true, mode: null, saved: 0 };

  if (dirHandle) {
    const allowed = await ensureWritePermission(dirHandle);
    if (!allowed) {
      return downloadAsZip(list, onItemProgress);
    }

    const usedNames = new Set();
    let saved = 0;
    for (const item of list) {
      try {
        const blob = await fetchMediaBlob(item, (percent) => {
          if (onItemProgress) onItemProgress(item, percent, false);
        });
        const fileName = getMediaDownloadName(item, blob, usedNames);
        await writeBlobToDirectory(dirHandle, fileName, blob);
        saved += 1;
        if (onItemProgress) onItemProgress(item, 100, true);
      } catch (err) {
        console.error("Media folder write failed", err);
        if (onItemProgress) onItemProgress(item, 100, true);
      }
    }
    return { cancelled: false, mode: "folder", saved };
  }

  return downloadAsZip(list, onItemProgress);
}
